/*
 * dewlab help centre: "Ask AI" chat + support tickets (UI only).
 *
 * Data source, in this order:
 *   1. window.dewlabSupportAdapter: an object you define with the same methods as `apiAdapter` below
 *   2. data-api: your support API base URL (contract in docs/dewlab-support-api.md)
 *   3. demo mode: sample data so the design can be reviewed (data-demo="true")
 */
(() => {
  if (customElements.get('skin-help-center')) return;

  const store = {
    get(key, fallback) {
      try {
        const v = window.localStorage.getItem(key);
        return v ? JSON.parse(v) : fallback;
      } catch (e) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        /* storage unavailable */
      }
    },
  };

  const escapeHtml = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  // Plain text → safe HTML with line breaks, **bold** and links
  const formatText = (s) =>
    escapeHtml(s)
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>')
      .replace(/\n/g, '<br>');

  const timeFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
  const dateFmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });
  const fullFmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

  const relative = (iso) => {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '';
    const mins = Math.round((Date.now() - t) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
    if (mins < 10080) return `${Math.round(mins / 1440)}d ago`;
    return dateFmt.format(new Date(t));
  };

  const STATUS = {
    open: { label: 'Open', cls: 'open' },
    pending: { label: 'Awaiting your reply', cls: 'pending' },
    resolved: { label: 'Resolved', cls: 'resolved' },
  };

  /* ------------------------------------------------------------------ API */
  const apiAdapter = (base) => {
    const url = (path) => base.replace(/\/$/, '') + path;
    const json = async (res) => {
      if (!res.ok) throw new Error((await res.text().catch(() => '')) || `Request failed (${res.status})`);
      return res.json();
    };
    const send = (path, method, body) =>
      fetch(url(path), {
        method,
        headers: body instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body instanceof FormData ? body : JSON.stringify(body),
      }).then(json);
    const qs = (params) =>
      '?' +
      Object.entries(params)
        .filter(([, v]) => v != null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');

    return {
      chat: (payload) => send('/ai/chat', 'POST', payload),
      listTickets: ({ customer, guest }) =>
        fetch(url('/tickets' + qs({ customer_id: customer.id, email: customer.email, ids: guest.map((g) => g.id).join(',') })), {
          headers: { Accept: 'application/json' },
        }).then(json),
      getTicket: (id, { email }) => fetch(url(`/tickets/${encodeURIComponent(id)}` + qs({ email })), { headers: { Accept: 'application/json' } }).then(json),
      createTicket: (formData) => send('/tickets', 'POST', formData),
      reply: (id, formData) => send(`/tickets/${encodeURIComponent(id)}/messages`, 'POST', formData),
      reopen: (id, payload) => send(`/tickets/${encodeURIComponent(id)}/reopen`, 'POST', payload),
      rate: (id, payload) => send(`/tickets/${encodeURIComponent(id)}/rating`, 'POST', payload),
      lookup: ({ email, number }) => fetch(url('/tickets/lookup' + qs({ email, number })), { headers: { Accept: 'application/json' } }).then(json),
    };
  };

  /* ----------------------------------------------------------------- DEMO */
  const demoAdapter = () => {
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    const ago = (h) => new Date(Date.now() - h * 3600000).toISOString();
    const tickets = [
      {
        id: 't_10482',
        number: 'DL-10482',
        subject: "My order hasn't arrived yet",
        category: 'Order & delivery',
        order_number: '#1042',
        status: 'pending',
        created_at: ago(30),
        updated_at: ago(2),
        unread: true,
        messages: [
          { id: 'm1', author: 'customer', name: 'You', body: "Hi, I ordered the 345 cream on Monday and tracking hasn't updated for 2 days. Can you check?", created_at: ago(30) },
          { id: 'e1', author: 'system', body: 'Ticket created', created_at: ago(30) },
          { id: 'm2', author: 'agent', name: 'Priya', role: 'dewlab support', body: "Hi! Sorry for the wait. I've checked with our courier: your parcel is at the Mumbai hub and goes out for delivery tomorrow.\n\nCould you confirm your phone number so the delivery partner can reach you?", created_at: ago(2) },
        ],
      },
      {
        id: 't_10391',
        number: 'DL-10391',
        subject: 'How do I check the batch code?',
        category: 'Product & skin advice',
        order_number: '#1018',
        status: 'resolved',
        created_at: ago(120),
        updated_at: ago(96),
        unread: false,
        messages: [
          { id: 'm1', author: 'customer', name: 'You', body: 'Where do I find the batch code on the tube?', created_at: ago(120) },
          { id: 'm2', author: 'agent', name: 'Rahul', role: 'dewlab support', body: "It's embossed on the crimped top edge of the tube, e.g. **EE025**. The same code is printed on the carton, and the two should match.", created_at: ago(118) },
          { id: 'm3', author: 'customer', name: 'You', body: 'Found it, they match. Thank you!', created_at: ago(97) },
          { id: 'e1', author: 'system', body: 'Marked as resolved', created_at: ago(96) },
        ],
      },
    ];
    let next = 10500;
    const strip = (t) => {
      const { messages, ...rest } = t;
      const last = messages[messages.length - 1];
      return { ...rest, last_message: last ? last.body : '' };
    };
    const answers = [
      [/order|track|deliver|arriv|ship/i, "You can track your order from the link in your shipping email or SMS. Orders usually arrive in **3–6 days** across India.\n\nIf tracking hasn't moved in 48 hours, tap **Talk to our team** and we'll check with the courier.", ['Raise a delivery ticket', 'How long is delivery?']],
      [/original|fake|real|authentic|batch/i, "Every tube arrives sealed, and we check the seal, batch code and expiry before dispatch. You'll find the batch code embossed on the top edge of the tube, matching the carton.\n\nIf anything arrives damaged, wrong or not genuine, report it within **7 days of delivery** with an unboxing video and we'll replace it or refund you.", ['Where is the batch code?', 'Shop the 345 cream']],
      [/red|sensitiv|irritat|acne|break|mark|spot/i, "For redness, breakouts and marks, start with the **Dr. Althea 345 Relief Cream**. It repairs the barrier first, so redness usually calms in **5–7 days** and marks look lighter in 2–4 weeks.\n\nUse a pea-sized amount morning and night as the last step before sunscreen.", ['Can I use it with retinol?', 'Is it good for oily skin?']],
      [/return|refund|exchange|money/i, "Returns are for **damaged, wrong or defective** items. Raise a ticket within **7 days of delivery** with your order number and an unboxing video, and we'll send a free replacement or refund you. For hygiene reasons we can't take back opened products that simply didn't suit you.", ['Raise a refund ticket', 'Is COD refund possible?']],
      [/cod|prepaid|pay|upi/i, "We accept UPI, cards, netbanking and cash on delivery. Paying online gets you an **extra 15% off**, and prepaid orders are packed first.", ['Where is my order?']],
    ];
    return {
      async chat({ message }) {
        await wait(900 + Math.random() * 700);
        const hit = answers.find(([re]) => re.test(message));
        if (hit) return { reply: hit[1], suggestions: hit[2] };
        return {
          reply: "I'm not completely sure about that one. Our team can help. Tap **Talk to our team** and I'll pass on this chat.",
          suggestions: ['Talk to our team'],
          handoff: true,
        };
      },
      async listTickets() {
        await wait(400);
        return tickets.map(strip);
      },
      async getTicket(id) {
        await wait(300);
        const t = tickets.find((x) => x.id === id);
        if (!t) throw new Error('Ticket not found');
        t.unread = false;
        return { ...t, messages: [...t.messages] };
      },
      async createTicket(fd) {
        await wait(800);
        const t = {
          id: `t_${next}`,
          number: `DL-${next++}`,
          subject: fd.get('subject'),
          category: fd.get('category'),
          order_number: fd.get('order_number'),
          status: 'open',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          unread: false,
          messages: [
            { id: 'm1', author: 'customer', name: 'You', body: fd.get('message'), created_at: new Date().toISOString(), attachments: fd.getAll('attachments').filter((f) => f && f.name).map((f) => ({ name: f.name })) },
            { id: 'e1', author: 'system', body: 'Ticket created', created_at: new Date().toISOString() },
          ],
        };
        tickets.unshift(t);
        return strip(t);
      },
      async reply(id, fd) {
        await wait(500);
        const t = tickets.find((x) => x.id === id);
        const msg = { id: `m${Date.now()}`, author: 'customer', name: 'You', body: fd.get('body'), created_at: new Date().toISOString(), attachments: fd.getAll('attachments').filter((f) => f && f.name).map((f) => ({ name: f.name })) };
        t.messages.push(msg);
        t.status = 'open';
        t.updated_at = msg.created_at;
        return msg;
      },
      async reopen(id) {
        await wait(400);
        const t = tickets.find((x) => x.id === id);
        t.status = 'open';
        t.messages.push({ id: `e${Date.now()}`, author: 'system', body: 'Ticket reopened', created_at: new Date().toISOString() });
        return strip(t);
      },
      async rate() {
        await wait(300);
        return { ok: true };
      },
      async lookup({ number }) {
        await wait(500);
        const t = tickets.find((x) => x.number.toLowerCase() === String(number).trim().toLowerCase());
        if (!t) throw new Error("We couldn't find that ticket. Check the number and email.");
        return strip(t);
      },
    };
  };


  /* ----------------------------------------------------------- SUPPORTHUB */
  // Bridges the Support Hub embed (window.SupportHub, docs: support-hub repo docs/embed-v0.md)
  // to this help centre. Tickets are read with the access token the hub returns, so the
  // token is the ticket id here and is remembered on this device.
  const GUEST_KEY = 'dewlab-help-guest-tickets';
  const CATEGORY_LABELS = {
    order: 'Order status',
    shipping: 'Shipping & delivery',
    refund: 'Return or refund',
    product: 'Product & skin advice',
    account: 'Account & payment',
    other: 'Something else',
  };

  const supportHubAdapter = (key, api) => {
    const hub = window.SupportHub.create({ key, api: api || undefined });
    const seenKey = `dewlab-help-seen-${key}`;
    const seen = () => store.get(seenKey, {});

    const status = (s) => {
      const v = String(s || '').toLowerCase();
      if (/resolv|solved|closed|done|archiv/.test(v)) return 'resolved';
      if (/pend|wait|await|hold|customer/.test(v)) return 'pending';
      return 'open';
    };
    const author = (m) => {
      const k = String(m.authorKind || m.author || m.role || '').toLowerCase();
      if (k === 'requester' || k === 'customer' || k === 'user') return 'customer';
      if (/ai|assistant|bot/.test(k)) return 'ai';
      if (/system|event|note/.test(k)) return 'system';
      return 'agent';
    };
    const when = (o) => o && (o.createdAt || o.created_at || o.sentAt || o.at || o.updatedAt || o.updated_at);
    const toMessage = (m, i) => {
      const a = author(m);
      return {
        id: m.id || `m${i}`,
        author: a,
        name: a === 'customer' ? 'You' : m.authorName || (a === 'ai' ? 'dewlab assistant' : 'dewlab support'),
        role: a === 'agent' ? 'dewlab support' : '',
        body: m.body || m.content || m.text || '',
        created_at: when(m) || new Date().toISOString(),
        attachments: m.attachments || [],
      };
    };
    const toTicket = (t, token) => {
      const messages = (t.messages || []).map(toMessage);
      const last = messages[messages.length - 1];
      const updated = t.updatedAt || t.updated_at || (last && last.created_at) || when(t);
      const lastSeen = seen()[token];
      const unread = !!(last && last.author !== 'customer' && (!lastSeen || new Date(last.created_at) > new Date(lastSeen)));
      return {
        id: token,
        number: t.displayId || t.number || '',
        subject: t.subject || 'Support request',
        category: CATEGORY_LABELS[t.category] || t.category || '',
        order_number: t.orderName || t.order_number || '',
        status: status(t.status),
        created_at: when(t) || (messages[0] && messages[0].created_at),
        updated_at: updated,
        unread,
        last_message: last ? last.body : '',
        messages,
      };
    };
    // The thread may come back flat or as { ticket, messages }
    const unwrap = (t) => {
      if (!t) return t;
      const inner = t.ticket || t.data || t;
      return { ...inner, messages: t.messages || inner.messages || [] };
    };
    const tokenOf = (r) => r && (r.accessToken || r.access_token || r.token || (r.ticket && r.ticket.accessToken));
    const markSeen = (token) => {
      const map = seen();
      map[token] = new Date().toISOString();
      store.set(seenKey, map);
    };

    return {
      kind: 'supporthub',
      hub,
      capabilities: { attachments: false, lookup: false, reopen: false, rate: false, stream: true },
      ready: hub.ready,
      async assistantEnabled() {
        const src = await hub.ready;
        return !!src.ai;
      },
      async categories() {
        const src = await hub.ready;
        return (src.categories || []).map((c) => ({ value: c, label: CATEGORY_LABELS[c] || c.charAt(0).toUpperCase() + c.slice(1) }));
      },
      async chat({ message, customer, onDelta, onStatus, onReset }) {
        await hub.ready;
        const res = await hub.ask(message, {
          email: customer.email || undefined,
          name: customer.name || undefined,
          onDelta,
          onStatus,
          onReset,
        });
        return { reply: res.reply, handoff: res.handoff || null, turnsLeft: res.turnsLeft };
      },
      async restoreChat() {
        await hub.ready;
        const chat = await hub.restoreChat();
        if (!chat || !chat.messages) return [];
        return chat.messages.map((m) => ({ role: m.role === 'user' ? 'user' : 'assistant', text: m.content || m.body || '', time: when(m) }));
      },
      resetChat() {
        hub.resetChat();
      },
      linkedTicket() {
        return hub.linkedToken();
      },
      async listTickets({ guest }) {
        await hub.ready;
        const results = await Promise.allSettled(guest.map((g) => hub.ticket(g.id).then((t) => (t ? toTicket(unwrap(t), g.id) : null))));
        // Forget tickets the hub no longer recognises (expired or invalid links)
        results.forEach((r, i) => {
          if (r.status === 'rejected' && r.reason && r.reason.status === 404) {
            store.set(GUEST_KEY, store.get(GUEST_KEY, []).filter((g) => g.id !== guest[i].id));
          }
        });
        return results.filter((r) => r.status === 'fulfilled' && r.value).map((r) => r.value);
      },
      async getTicket(token) {
        const t = unwrap(await hub.ticket(token));
        if (!t) throw new Error('This ticket could not be found.');
        markSeen(token);
        return { ...toTicket(t, token), unread: false };
      },
      async createTicket(fd) {
        await hub.ready;
        const r = await hub.createTicket({
          name: fd.get('name'),
          email: fd.get('email'),
          subject: fd.get('subject'),
          body: fd.get('message'),
          category: fd.get('category') || 'other',
          orderName: fd.get('order_number') || undefined,
          website: fd.get('website') || '',
          chatId: fd.get('carry_chat') === '1' ? undefined : null,
          meta: { page: window.location.pathname, customerId: fd.get('customer_id') || undefined },
        });
        const token = tokenOf(r);
        if (token) markSeen(token);
        const now = new Date().toISOString();
        return {
          id: token || `pending-${r.displayId || Date.now()}`,
          trackable: !!token,
          number: r.displayId || r.number || '',
          subject: fd.get('subject'),
          category: CATEGORY_LABELS[fd.get('category')] || fd.get('category'),
          order_number: fd.get('order_number'),
          status: 'open',
          created_at: now,
          updated_at: now,
          unread: false,
          last_message: fd.get('message'),
        };
      },
      async reply(token, fd) {
        const res = await hub.reply(fd.get('body'), token);
        markSeen(token);
        let t = unwrap(res);
        // Some responses only acknowledge the message; re-read the thread so nothing disappears
        if (!t || !t.messages || !t.messages.length) t = unwrap(await hub.ticket(token));
        const mapped = toTicket(t, token);
        return { message: mapped.messages[mapped.messages.length - 1], ticket: mapped.messages.length ? mapped : null };
      },
    };
  };

  /* ------------------------------------------------------------ ELEMENT */
  class SkinHelpCenter extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const d = this.dataset;
      this.customer = { id: d.customerId || '', email: d.customerEmail || '', name: d.customerName || '' };
      const useHub = d.hubKey && window.SupportHub && typeof window.SupportHub.create === 'function';
      this.adapter =
        window.dewlabSupportAdapter ||
        (useHub ? supportHubAdapter(d.hubKey, d.hubApi) : d.api ? apiAdapter(d.api) : d.demo === 'true' ? demoAdapter() : null);
      this.isDemo = !window.dewlabSupportAdapter && !useHub && !d.api && d.demo === 'true';
      this.caps = Object.assign({ attachments: true, lookup: true, reopen: true, rate: true }, (this.adapter && this.adapter.capabilities) || {});
      this.sessionKey = 'dewlab-help-chat';
      this.guestKey = GUEST_KEY;
      this.filter = 'open';
      this.tickets = [];
      this.current = null;

      this.setupTabs();
      this.setupChat();
      this.setupTickets();
      this.applyCapabilities();
      const tab = window.location.hash === '#tickets' ? 'tickets' : d.defaultTab || 'ai';
      this.showTab(tab, false);
      this.connectAdapter();
    }

    // Hide controls the connected support system doesn't offer
    applyCapabilities() {
      if (!this.caps.attachments) this.querySelectorAll('.skin-thread__attach').forEach((el) => (el.hidden = true));
      if (!this.caps.lookup) {
        const lookup = this.$('[data-ticket-lookup]');
        if (lookup) lookup.hidden = true;
      }
      if (!this.caps.reopen) {
        const reopen = this.$('[data-thread-reopen]');
        if (reopen) reopen.hidden = true;
      }
      if (!this.caps.rate) {
        this.querySelectorAll('[data-rate]').forEach((b) => (b.hidden = true));
        const resolved = this.$('[data-thread-resolved] p');
        if (resolved) resolved.innerHTML = '<strong>This ticket is resolved.</strong> Need more help? Raise a new ticket.';
      }
    }

    // The assistant is switched off in the support console: show tickets only
    disableAssistant() {
      this.assistantOff = true;
      const aiTab = this.$('[data-tab="ai"]');
      const tabs = this.$('.skin-help__tabs');
      if (aiTab) aiTab.hidden = true;
      if (tabs) tabs.hidden = true;
      this.showTab('tickets', false);
    }

    async connectAdapter() {
      const a = this.adapter;
      if (!a) return;
      if (a.ready) {
        try {
          await a.ready;
        } catch (err) {
          this.classList.add('is-offline');
          const foot = this.$('.skin-chat__foot');
          if (foot) foot.insertAdjacentHTML('afterbegin', '<strong>Support is offline right now.</strong> ');
          return;
        }
      }
      if (a.assistantEnabled) {
        try {
          if (!(await a.assistantEnabled())) this.disableAssistant();
        } catch (err) {
          /* leave the assistant visible */
        }
      }
      if (a.categories) {
        try {
          const cats = await a.categories();
          const select = this.form && this.form.elements.category;
          if (select && cats.length) select.innerHTML = cats.map((c) => `<option value="${escapeHtml(c.value)}">${escapeHtml(c.label)}</option>`).join('');
        } catch (err) {
          /* keep the topics from the theme editor */
        }
      }
      if (a.restoreChat && !this.history.length) {
        try {
          const restored = await a.restoreChat();
          if (restored && restored.length) {
            this.history = restored;
            this.saveChat();
            this.renderChat();
            this.setSuggestions([]);
          }
        } catch (err) {
          /* start fresh */
        }
      }
      const linked = a.linkedTicket && a.linkedTicket();
      if (linked) {
        this.rememberGuest({ id: linked, number: '' }, '');
        this.showTab('tickets', false);
        await this.loadTickets();
        this.openTicket(linked);
      }
    }

    $(sel) {
      return this.querySelector(sel);
    }

    /* Tabs */
    setupTabs() {
      this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.showTab(tab.dataset.tab, true));
        tab.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const i = this.tabs.indexOf(tab);
          const next = this.tabs[(i + (e.key === 'ArrowRight' ? 1 : this.tabs.length - 1)) % this.tabs.length];
          next.focus();
          this.showTab(next.dataset.tab, true);
        });
      });
    }

    showTab(name, focusPanel) {
      if (this.assistantOff && name === 'ai') name = 'tickets';
      this.tabs.forEach((t) => {
        const on = t.dataset.tab === name;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      this.querySelectorAll('[data-panel]').forEach((p) => (p.hidden = p.dataset.panel !== name));
      if (name === 'tickets' && !this.ticketsLoaded) this.loadTickets();
      if (name === 'ai' && focusPanel && window.matchMedia('(hover: hover)').matches) this.chatInput.focus({ preventScroll: true });
    }

    /* ------------------------------------------------------------ Chat */
    setupChat() {
      this.chatLog = this.$('[data-chat-log]');
      this.chatForm = this.$('[data-chat-form]');
      this.chatInput = this.$('[data-chat-input]');
      this.chatSend = this.$('[data-chat-send]');
      this.chatSuggest = this.$('[data-chat-suggestions]');
      this.defaultSuggestions = Array.from(this.chatSuggest.querySelectorAll('[data-suggestion]')).map((b) => b.textContent.trim());
      this.history = [];
      try {
        this.history = JSON.parse(window.sessionStorage.getItem(this.sessionKey) || '[]');
      } catch (e) {
        this.history = [];
      }
      this.sessionId = this.chatSession();

      this.renderChat();

      this.chatInput.addEventListener('input', () => {
        this.autoGrow(this.chatInput);
        this.chatSend.disabled = !this.chatInput.value.trim() || this.chatBusy;
      });
      this.chatInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.chatForm.requestSubmit();
        }
      });
      this.chatForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.askAI(this.chatInput.value);
      });
      this.chatSuggest.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-suggestion]');
        if (!chip) return;
        if (/talk to our team|raise .* ticket/i.test(chip.textContent)) return this.handoff();
        this.askAI(chip.textContent.trim());
      });
      this.$('[data-chat-reset]').addEventListener('click', () => {
        this.history = [];
        this.lastHandoff = null;
        if (this.adapter && this.adapter.resetChat) this.adapter.resetChat();
        this.sessionId = this.chatSession(true);
        this.saveChat();
        this.renderChat();
        this.setSuggestions(this.defaultSuggestions);
      });
      this.$('[data-handoff]').addEventListener('click', () => this.handoff());
    }

    chatSession(fresh) {
      const key = `${this.sessionKey}-id`;
      let id = '';
      try {
        id = fresh ? '' : window.sessionStorage.getItem(key) || '';
        if (!id) {
          id = `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
          window.sessionStorage.setItem(key, id);
        }
      } catch (e) {
        id = id || `s_${Date.now().toString(36)}`;
      }
      return id;
    }

    saveChat() {
      try {
        window.sessionStorage.setItem(this.sessionKey, JSON.stringify(this.history));
      } catch (e) {
        /* storage unavailable */
      }
    }

    autoGrow(el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }

    bubble(role, text, time) {
      const row = document.createElement('div');
      row.className = `skin-chat__row skin-chat__row--${role}`;
      row.innerHTML = `
        ${role === 'ai' ? '<span class="skin-chat__mini" aria-hidden="true">✦</span>' : ''}
        <div class="skin-chat__bubble">${formatText(text)}${time ? `<time>${timeFmt.format(new Date(time))}</time>` : ''}</div>`;
      return row;
    }

    renderChat() {
      this.chatLog.innerHTML = '';
      const greet = this.dataset.aiGreeting;
      const name = this.customer.name ? `Hi ${this.customer.name}! ` : '';
      if (greet) this.chatLog.appendChild(this.bubble('ai', name ? greet.replace(/^Hi!?\s*/i, name) : greet));
      this.history.forEach((m) => this.chatLog.appendChild(this.bubble(m.role === 'user' ? 'user' : 'ai', m.text, m.time)));
      this.chatSuggest.hidden = this.history.length > 0 && !this.lastSuggestions;
      this.scrollChat();
    }

    setSuggestions(list) {
      this.lastSuggestions = list && list.length ? list : null;
      this.chatSuggest.innerHTML = '';
      (list || []).forEach((s) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'skin-chat__chip';
        b.dataset.suggestion = '';
        b.textContent = s;
        this.chatSuggest.appendChild(b);
      });
      this.chatSuggest.hidden = !this.lastSuggestions;
    }

    scrollChat() {
      requestAnimationFrame(() => (this.chatLog.scrollTop = this.chatLog.scrollHeight));
    }

    async askAI(raw) {
      const text = String(raw || '').trim();
      if (!text || this.chatBusy) return;
      const now = new Date().toISOString();
      this.history.push({ role: 'user', text, time: now });
      this.saveChat();
      this.chatLog.appendChild(this.bubble('user', text, now));
      this.chatInput.value = '';
      this.autoGrow(this.chatInput);
      this.chatSend.disabled = true;
      this.setSuggestions([]);

      const typing = document.createElement('div');
      typing.className = 'skin-chat__row skin-chat__row--ai';
      typing.innerHTML = '<span class="skin-chat__mini" aria-hidden="true">✦</span><div class="skin-chat__bubble skin-chat__typing" aria-label="Assistant is typing"><i></i><i></i><i></i></div>';
      this.chatLog.appendChild(typing);
      this.scrollChat();
      this.chatBusy = true;

      let live = null;
      let streamed = '';
      const liveBubble = () => {
        if (!live) {
          typing.remove();
          live = this.bubble('ai', '');
          this.chatLog.appendChild(live);
        }
        return live.querySelector('.skin-chat__bubble');
      };
      try {
        if (!this.adapter) throw new Error('not connected');
        const res = await this.adapter.chat({
          session_id: this.sessionId,
          message: text,
          history: this.history.slice(-12).map((m) => ({ role: m.role, content: m.text })),
          customer: this.customer,
          page_url: window.location.href,
          shop: this.dataset.shop,
          onStatus: (status) => {
            if (streamed || !status) return;
            typing.querySelector('.skin-chat__bubble').innerHTML = `<span class="skin-chat__status-text">${escapeHtml(status)}</span><i></i><i></i><i></i>`;
            this.scrollChat();
          },
          onDelta: (chunk) => {
            streamed += chunk;
            liveBubble().innerHTML = formatText(streamed);
            this.chatLog.scrollTop = this.chatLog.scrollHeight;
          },
          onReset: () => {
            streamed = '';
            if (live) liveBubble().innerHTML = '';
          },
        });
        typing.remove();
        const reply = (res && (res.reply || res.message)) || streamed || "Sorry, I couldn't find an answer to that.";
        const time = new Date().toISOString();
        this.history.push({ role: 'assistant', text: reply, time });
        this.saveChat();
        const finalRow = this.bubble('ai', reply, time);
        if (live) live.replaceWith(finalRow);
        else this.chatLog.appendChild(finalRow);
        const chips = (res && res.suggestions) || [];
        this.lastHandoff = res && res.handoff && typeof res.handoff === 'object' ? res.handoff : this.lastHandoff;
        if (res && res.handoff && !chips.some((c) => /team/i.test(c))) chips.push('Talk to our team');
        this.setSuggestions(chips);
        if (res && typeof res.turnsLeft === 'number' && res.turnsLeft <= 2) {
          const note = document.createElement('p');
          note.className = 'skin-chat__note';
          note.textContent =
            res.turnsLeft > 0
              ? `${res.turnsLeft} more message${res.turnsLeft === 1 ? '' : 's'} in this chat. For more help, talk to our team.`
              : 'This chat has reached its limit. Talk to our team and we will take it from here.';
          this.chatLog.appendChild(note);
          if (res.turnsLeft <= 0) this.setSuggestions(['Talk to our team']);
        }
      } catch (err) {
        typing.remove();
        if (live) live.remove();
        const msg = err && err.name === 'SupportHubError' && err.message ? err.message : "I'm having trouble connecting right now. Please try again, or raise a ticket and our team will reply.";
        const row = this.bubble('ai', msg);
        row.classList.add('skin-chat__row--error');
        this.chatLog.appendChild(row);
        this.setSuggestions(['Talk to our team']);
      } finally {
        this.chatBusy = false;
        this.chatSend.disabled = !this.chatInput.value.trim();
        this.scrollChat();
      }
    }

    handoff() {
      const userMsgs = this.history.filter((m) => m.role === 'user').map((m) => m.text);
      const h = this.lastHandoff;
      this.showTab('tickets', false);
      if (this.adapter && this.adapter.kind === 'supporthub') {
        // The hub carries the whole chat onto the ticket, so only the summary is needed here
        this.openForm({
          subject: (h && h.subject) || (userMsgs.length ? userMsgs[userMsgs.length - 1].slice(0, 110) : ''),
          message: (h && h.summary) || (userMsgs.length ? userMsgs[userMsgs.length - 1] : ''),
          category: h && h.category,
          carryChat: userMsgs.length > 0,
        });
        return;
      }
      const transcript = this.history.map((m) => `${m.role === 'user' ? 'Me' : 'Assistant'}: ${m.text}`).join('\n');
      this.openForm({
        subject: userMsgs.length ? userMsgs[userMsgs.length - 1].slice(0, 110) : '',
        message: userMsgs.length ? `${userMsgs[userMsgs.length - 1]}\n\n--- Chat with assistant ---\n${transcript}` : '',
      });
    }

    /* --------------------------------------------------------- Tickets */
    setupTickets() {
      this.layout = this.$('.skin-tickets');
      this.list = this.$('[data-ticket-list]');
      this.empty = this.$('[data-ticket-empty]');
      this.placeholder = this.$('[data-ticket-placeholder]');
      this.thread = this.$('[data-thread]');
      this.form = this.$('[data-ticket-form]');
      this.done = this.$('[data-ticket-done]');
      this.count = this.$('[data-ticket-count]');
      this.replyForm = this.$('[data-thread-form]');
      this.replyInput = this.$('[data-thread-input]');
      this.replySend = this.$('[data-thread-send]');

      this.$('[data-ticket-new]').addEventListener('click', () => this.openForm());
      this.querySelectorAll('[data-thread-back]').forEach((b) => b.addEventListener('click', () => this.backToList()));
      this.querySelectorAll('[data-ticket-filter]').forEach((b) =>
        b.addEventListener('click', () => {
          this.filter = b.dataset.ticketFilter;
          this.querySelectorAll('[data-ticket-filter]').forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
          this.renderList();
        })
      );
      this.list.addEventListener('click', (e) => {
        const item = e.target.closest('[data-ticket-id]');
        if (item) this.openTicket(item.dataset.ticketId);
      });

      // Attachment labels show the chosen file names
      this.querySelectorAll('input[type="file"]').forEach((input) => {
        const label = input.closest('label').querySelector('[data-attach-label]');
        const original = label.textContent;
        input.addEventListener('change', () => {
          const n = input.files.length;
          label.textContent = n ? (n === 1 ? input.files[0].name : `${n} files attached`) : original;
          input.closest('label').classList.toggle('has-files', n > 0);
        });
      });

      this.replyInput.addEventListener('input', () => {
        this.autoGrow(this.replyInput);
        this.replySend.disabled = !this.replyInput.value.trim();
      });
      this.replyForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.sendReply();
      });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submitTicket();
      });
      this.$('[data-thread-reopen]').addEventListener('click', () => this.reopen());
      this.querySelectorAll('[data-rate]').forEach((b) =>
        b.addEventListener('click', async () => {
          this.querySelectorAll('[data-rate]').forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
          try {
            await this.adapter.rate(this.current.id, { value: b.dataset.rate, email: this.customer.email || this.current.email });
          } catch (err) {
            /* rating is best effort */
          }
        })
      );
      this.$('[data-done-open]').addEventListener('click', () => this.created && this.openTicket(this.created.id));

      const lookup = this.$('[data-ticket-lookup]');
      if (lookup) {
        lookup.addEventListener('submit', async (e) => {
          e.preventDefault();
          const fd = new FormData(lookup);
          const btn = lookup.querySelector('button');
          btn.disabled = true;
          try {
            const t = await this.adapter.lookup({ email: fd.get('email'), number: fd.get('number') });
            this.rememberGuest(t, fd.get('email'));
            this.upsert(t);
            this.renderList();
            this.openTicket(t.id);
            lookup.reset();
          } catch (err) {
            window.alert(err.message || "We couldn't find that ticket.");
          } finally {
            btn.disabled = false;
          }
        });
      }
    }

    rememberGuest(t, email) {
      if (this.customer.id && !(this.adapter && this.adapter.kind === 'supporthub')) return;
      const list = store.get(this.guestKey, []).filter((g) => g.id !== t.id);
      list.unshift({ id: t.id, number: t.number, email });
      store.set(this.guestKey, list.slice(0, 20));
    }

    upsert(t) {
      const i = this.tickets.findIndex((x) => x.id === t.id);
      if (i >= 0) this.tickets[i] = { ...this.tickets[i], ...t };
      else this.tickets.unshift(t);
    }

    async loadTickets() {
      this.ticketsLoaded = true;
      this.list.innerHTML = '<li class="skin-tickets__loading"><span></span><span></span><span></span></li>';
      try {
        if (!this.adapter) throw new Error('not connected');
        const res = await this.adapter.listTickets({ customer: this.customer, guest: store.get(this.guestKey, []) });
        this.tickets = Array.isArray(res) ? res : (res && res.tickets) || [];
      } catch (err) {
        this.tickets = [];
      }
      this.renderList();
    }

    renderList() {
      const visible = this.tickets
        .filter((t) => this.filter === 'all' || (this.filter === 'resolved' ? t.status === 'resolved' : t.status !== 'resolved'))
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      const unread = this.tickets.filter((t) => t.unread).length;
      this.count.hidden = !unread;
      this.count.textContent = unread;
      this.empty.hidden = visible.length > 0;
      if (!visible.length && this.tickets.length) {
        this.empty.querySelector('strong').textContent = this.filter === 'resolved' ? 'No resolved tickets.' : 'No open tickets.';
      }
      this.list.innerHTML = visible
        .map((t) => {
          const s = STATUS[t.status] || STATUS.open;
          return `<li>
            <button type="button" class="skin-ticket-item${t.unread ? ' is-unread' : ''}${this.current && this.current.id === t.id ? ' is-active' : ''}" data-ticket-id="${escapeHtml(t.id)}">
              <span class="skin-ticket-item__top">
                <span class="skin-ticket-item__number">${escapeHtml(t.number || '')}</span>
                <span class="skin-ticket-item__time">${escapeHtml(relative(t.updated_at))}</span>
              </span>
              <span class="skin-ticket-item__subject">${escapeHtml(t.subject)}</span>
              ${t.last_message ? `<span class="skin-ticket-item__preview">${escapeHtml(t.last_message)}</span>` : ''}
              <span class="skin-ticket-item__bottom">
                <span class="skin-status skin-status--${s.cls}">${s.label}</span>
                ${t.unread ? '<span class="skin-ticket-item__new">New reply</span>' : ''}
              </span>
            </button>
          </li>`;
        })
        .join('');
    }

    showMain(which) {
      this.placeholder.hidden = which !== 'placeholder';
      this.thread.hidden = which !== 'thread';
      this.form.hidden = which !== 'form';
      this.done.hidden = which !== 'done';
      this.layout.dataset.view = which === 'placeholder' ? 'list' : 'detail';
      if (which !== 'placeholder' && window.matchMedia('(max-width: 749px)').matches) {
        this.layout.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    backToList() {
      clearInterval(this.watchTimer);
      this.current = null;
      this.showMain('placeholder');
      this.renderList();
    }

    openForm(prefill = {}) {
      clearInterval(this.watchTimer);
      this.current = null;
      this.renderList();
      this.form.reset();
      this.$('[data-ticket-error]').hidden = true;
      this.form.querySelectorAll('[data-attach-label]').forEach((l) => (l.textContent = 'Add photos (optional)'));
      if (this.customer.email) this.form.elements.email.value = this.customer.email;
      if (prefill.subject) this.form.elements.subject.value = prefill.subject;
      if (prefill.message) this.form.elements.message.value = prefill.message;
      if (prefill.category && this.form.elements.category) {
        const opt = Array.from(this.form.elements.category.options).find((o) => o.value === prefill.category);
        if (opt) this.form.elements.category.value = prefill.category;
      }
      this.carryChat = !!prefill.carryChat;
      this.showMain('form');
      (prefill.subject ? this.form.elements.message : this.form.elements.name.value ? this.form.elements.subject : this.form.elements.name).focus({ preventScroll: true });
    }

    async submitTicket() {
      const err = this.$('[data-ticket-error]');
      const required = ['name', 'email', 'subject', 'message'];
      const missing = required.filter((n) => !String(this.form.elements[n].value).trim());
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.elements.email.value);
      this.form.querySelectorAll('.skin-field').forEach((f) => f.classList.remove('has-error'));
      if (missing.length || !emailOk) {
        missing.concat(emailOk ? [] : ['email']).forEach((n) => this.form.elements[n].closest('.skin-field').classList.add('has-error'));
        err.textContent = !emailOk && !missing.length ? 'Please enter a valid email address.' : 'Please fill in the highlighted fields.';
        err.hidden = false;
        return;
      }
      err.hidden = true;
      const btn = this.form.querySelector('[type="submit"]');
      btn.disabled = true;
      btn.classList.add('is-loading');
      try {
        if (!this.adapter) throw new Error('Support is not connected yet. Please email us instead.');
        const fd = new FormData(this.form);
        fd.append('customer_id', this.customer.id);
        fd.append('carry_chat', this.carryChat ? '1' : '0');
        fd.append('page_url', window.location.href);
        if (this.history.length) fd.append('chat_session_id', this.sessionId);
        const t = await this.adapter.createTicket(fd);
        this.created = t;
        try {
          if (t.trackable !== false) {
            this.rememberGuest(t, fd.get('email'));
            this.upsert(t);
            this.renderList();
          }
        } catch (uiErr) {
          /* the ticket exists; the list will catch up on the next load */
        }
        this.$('[data-done-number]').textContent = t.number || '';
        const openBtn = this.$('[data-done-open]');
        if (openBtn) openBtn.hidden = t.trackable === false;
        if (this.carryChat) {
          // The conversation now lives on the ticket; start the next chat fresh
          this.history = [];
          this.lastHandoff = null;
          this.saveChat();
          this.renderChat();
          this.setSuggestions(this.defaultSuggestions);
          this.carryChat = false;
        }
        this.showMain('done');
      } catch (e) {
        // A server error after submitting can still mean the ticket was saved (e.g. the receipt email failed)
        const maybeSaved = e && e.status >= 500;
        err.textContent = maybeSaved
          ? `${e.message || 'Something went wrong.'} Your ticket may still have reached us. Check your email for a confirmation before sending it again.`
          : (e && e.message) || 'Something went wrong. Please try again.';
        err.hidden = false;
        if (window.console) console.error('[help centre] ticket submit failed', e);
      } finally {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    }

    messageHtml(m) {
      if (m.author === 'system') {
        return `<div class="skin-msg skin-msg--system"><span>${escapeHtml(m.body)} · ${escapeHtml(fullFmt.format(new Date(m.created_at)))}</span></div>`;
      }
      const mine = m.author === 'customer';
      const files = (m.attachments || [])
        .map((a) => (a.url ? `<a class="skin-msg__file" href="${escapeHtml(a.url)}" target="_blank" rel="noopener">📎 ${escapeHtml(a.name || 'Attachment')}</a>` : `<span class="skin-msg__file">📎 ${escapeHtml(a.name || 'Attachment')}</span>`))
        .join('');
      const who = mine ? 'You' : `${escapeHtml(m.name || 'dewlab support')}${m.role ? ` <span>· ${escapeHtml(m.role)}</span>` : ''}`;
      const initial = escapeHtml((m.name || 'D').trim().charAt(0).toUpperCase());
      return `<div class="skin-msg skin-msg--${mine ? 'me' : m.author === 'ai' ? 'ai' : 'agent'}">
        ${mine ? '' : `<span class="skin-msg__avatar" aria-hidden="true">${m.author === 'ai' ? '✦' : initial}</span>`}
        <div class="skin-msg__body">
          <p class="skin-msg__who">${who}<time>${escapeHtml(fullFmt.format(new Date(m.created_at)))}</time></p>
          <div class="skin-msg__bubble">${formatText(m.body)}${files ? `<div class="skin-msg__files">${files}</div>` : ''}</div>
        </div>
      </div>`;
    }

    renderThread(t) {
      const s = STATUS[t.status] || STATUS.open;
      this.$('[data-thread-number]').textContent = `${t.number || ''}${t.category ? ` · ${t.category}` : ''}`;
      this.$('[data-thread-subject]').textContent = t.subject || '';
      this.$('[data-thread-info]').textContent = [t.order_number ? `Order ${t.order_number}` : '', t.created_at ? `Opened ${dateFmt.format(new Date(t.created_at))}` : '']
        .filter(Boolean)
        .join(' · ');
      const status = this.$('[data-thread-status]');
      status.className = `skin-status skin-status--${s.cls}`;
      status.textContent = s.label;
      const log = this.$('[data-thread-log]');
      log.innerHTML = (t.messages || []).map((m) => this.messageHtml(m)).join('');
      const resolved = t.status === 'resolved';
      this.replyForm.hidden = resolved;
      this.$('[data-thread-resolved]').hidden = !resolved;
      requestAnimationFrame(() => (log.scrollTop = log.scrollHeight));
    }

    async openTicket(id) {
      const summary = this.tickets.find((t) => t.id === id) || { id };
      this.current = summary;
      this.renderList();
      this.showMain('thread');
      this.renderThread({ ...summary, messages: [] });
      const log = this.$('[data-thread-log]');
      log.innerHTML = '<div class="skin-tickets__loading"><span></span><span></span><span></span></div>';
      try {
        const guest = store.get(this.guestKey, []).find((g) => g.id === id);
        const full = await this.adapter.getTicket(id, { email: this.customer.email || (guest && guest.email) || '' });
        const t = full.ticket ? { ...full.ticket, messages: full.messages || full.ticket.messages || [] } : full;
        this.current = t;
        this.upsert({ ...t, unread: false });
        this.renderList();
        this.renderThread(t);
        this.watchThread(id);
      } catch (err) {
        log.innerHTML = `<p class="skin-msg skin-msg--system"><span>${escapeHtml(err.message || 'Could not load this ticket.')}</span></p>`;
      }
    }

    // Re-read the open ticket every 20s (while the tab is visible) so agent replies appear on their own
    watchThread(id) {
      clearInterval(this.watchTimer);
      this.watchTimer = setInterval(async () => {
        if (document.hidden || !this.current || this.current.id !== id || this.thread.hidden) return;
        try {
          const full = await this.adapter.getTicket(id, { email: this.customer.email || '' });
          const t = full.ticket ? { ...full.ticket, messages: full.messages || full.ticket.messages || [] } : full;
          if (!this.current || this.current.id !== id) return;
          const before = (this.current.messages || []).length;
          if ((t.messages || []).length !== before || t.status !== this.current.status) {
            const draft = this.replyInput.value;
            this.current = t;
            this.upsert({ ...t, unread: false });
            this.renderList();
            this.renderThread(t);
            this.replyInput.value = draft;
          }
        } catch (err) {
          /* try again on the next tick */
        }
      }, 20000);
    }

    async sendReply() {
      const body = this.replyInput.value.trim();
      if (!body || !this.current) return;
      const files = this.$('[data-thread-files]');
      const fd = new FormData();
      fd.append('body', body);
      fd.append('email', this.customer.email || '');
      Array.from(files.files).forEach((f) => fd.append('attachments', f));
      const log = this.$('[data-thread-log]');
      const optimistic = { author: 'customer', name: 'You', body, created_at: new Date().toISOString(), attachments: Array.from(files.files).map((f) => ({ name: f.name })) };
      const wrap = document.createElement('div');
      wrap.innerHTML = this.messageHtml(optimistic);
      const node = wrap.firstElementChild;
      node.classList.add('is-sending');
      log.appendChild(node);
      log.scrollTop = log.scrollHeight;
      this.replyInput.value = '';
      this.autoGrow(this.replyInput);
      this.replySend.disabled = true;
      files.value = '';
      files.dispatchEvent(new Event('change'));
      try {
        const saved = await this.adapter.reply(this.current.id, fd);
        node.classList.remove('is-sending');
        const shown = (this.current.messages || []).length + 1;
        if (saved && saved.ticket && (saved.ticket.messages || []).length >= shown) {
          this.current = saved.ticket;
          this.upsert(saved.ticket);
          this.renderThread(saved.ticket);
        } else {
          const savedMsg = saved && 'ticket' in saved ? saved.message : saved;
          this.current.messages = (this.current.messages || []).concat(savedMsg || optimistic);
        }
        this.current.status = 'open';
        this.upsert({ id: this.current.id, status: 'open', updated_at: new Date().toISOString(), last_message: body });
        this.renderList();
        const status = this.$('[data-thread-status]');
        status.className = 'skin-status skin-status--open';
        status.textContent = STATUS.open.label;
      } catch (err) {
        node.classList.remove('is-sending');
        node.classList.add('is-failed');
        node.querySelector('.skin-msg__body').insertAdjacentHTML('beforeend', '<p class="skin-msg__failed">Not sent. Check your connection and try again.</p>');
        this.replyInput.value = body;
        this.replySend.disabled = false;
      }
    }

    async reopen() {
      if (!this.current) return;
      try {
        const t = await this.adapter.reopen(this.current.id, { email: this.customer.email });
        this.upsert(t);
        this.openTicket(this.current.id);
      } catch (err) {
        window.alert('Could not reopen this ticket. Please try again.');
      }
    }
  }

  customElements.define('skin-help-center', SkinHelpCenter);
})();
